import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, calendarAPI, lessonsAPI, meetAPI } from '../../services/api';
import './StudentPages.css';

const toYmd = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toDateKey = (dateObj) => {
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const BookLesson = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedDate, setSelectedDate] = useState(toYmd(new Date()));
  const [availability, setAvailability] = useState({});

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === selectedCourseId) || null,
    [courses, selectedCourseId]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [teachersRes, coursesRes] = await Promise.all([
          usersAPI.getTeachers(),
          lessonsAPI.getCourses()
        ]);
        setTeachers(teachersRes.data.teachers || []);
        setCourses(coursesRes.data.courses || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadAvailability = async () => {
      if (!selectedDate || teachers.length === 0) {
        setAvailability({});
        return;
      }

      try {
        setCalendarLoading(true);
        const results = await Promise.all(
          teachers.map(async (t) => {
            try {
              const res = await calendarAPI.getTeacherAvailability(t.id, selectedDate, { explicitOnly: 'true' });
              return [t.id, res.data.availableSlots || []];
            } catch (e) {
              return [t.id, []];
            }
          })
        );

        const map = {};
        results.forEach(([teacherId, slots]) => {
          // slots: [{start,end}]
          const hours = new Set();
          slots.forEach((s) => {
            const d = new Date(s.start);
            if (toDateKey(d) !== selectedDate) return;
            hours.add(d.getHours());
          });
          map[teacherId] = hours;
        });
        setAvailability(map);
      } finally {
        setCalendarLoading(false);
      }
    };

    loadAvailability();
  }, [selectedDate, teachers]);

  const handleCellClick = async (teacher, hour) => {
    if (booking) return;
    const start = new Date(selectedDate);
    start.setHours(hour, 0, 0, 0);
    const label = start.toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    if (!window.confirm(`Book a lesson with ${teacher.name}?\n${label}`)) {
      return;
    }

    try {
      setBooking(true);
      await meetAPI.bookLesson({
        teacherId: teacher.id,
        lessonId: null,
        scheduledAt: start.toISOString(),
        durationMinutes: 60,
        title: selectedCourse ? `Japanese Lesson - ${selectedCourse.title}` : undefined
      });
      navigate('/student/bookings');
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Booking failed';
      alert(message);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ height: '50vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="courses-page fade-in">
      <h1>📅 Book a Lesson</h1>
      <p className="page-subtitle">Select a date and click on an available slot to book</p>

      <div className="booking-toolbar">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Date</label>
          <input
            type="date"
            className="form-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            required
          />
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Course (Optional)</label>
          <select
            className="form-select"
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
          >
            <option value="">No course selected</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.level} - {c.title}
              </option>
            ))}
          </select>
        </div>

        <div className="booking-legend">
          <span className="legend-item"><span className="legend-dot available" /> Available</span>
          <span className="legend-item"><span className="legend-dot unavailable" /> Unavailable</span>
        </div>
      </div>

      {teachers.length === 0 ? (
        <div className="empty-state">
          <p>No teachers available</p>
        </div>
      ) : calendarLoading ? (
        <div className="booking-calendar-loading">Loading...</div>
      ) : (
        <div className="booking-calendar">
          <div className="booking-grid">
            <div className="booking-grid-header">
              <div className="booking-header-cell time" />
              {teachers.map((t) => {
                const count = availability?.[t.id]?.size || 0;
                return (
                  <div
                    key={t.id}
                    className={`booking-header-cell teacher ${count === 0 ? 'muted' : ''}`}
                    title={t.name}
                  >
                    <div className="teacher-name">{t.name}</div>
                    <div className="teacher-meta">{count} slots</div>
                  </div>
                );
              })}
            </div>

            {Array.from({ length: 12 }).map((_, idx) => {
              const hour = 9 + idx;
              return (
                <div key={hour} className="booking-grid-row">
                  <div className="booking-time-cell">{String(hour).padStart(2, '0')}:00</div>
                  {teachers.map((t) => {
                    const isAvailable = availability?.[t.id]?.has(hour);
                    return (
                      <button
                        key={`${hour}-${t.id}`}
                        type="button"
                        className={`booking-cell ${isAvailable ? 'available' : 'unavailable'}`}
                        disabled={!isAvailable || booking}
                        onClick={() => isAvailable && handleCellClick(t, hour)}
                        title={isAvailable ? 'Click to book' : 'Not available'}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="modal-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-outline" onClick={() => navigate('/student')} disabled={booking}>
          Back
        </button>
      </div>
    </div>
  );
};

export default BookLesson;
